/**
 * Onboarding Steps Utilities
 *
 * Centralizes the hardcoded 7-step onboarding definitions that were
 * previously duplicated across Admin.jsx, Jobs.jsx, and AppContext.jsx.
 *
 * This module handles the OLD flat-column format (onboarding_checklist table).
 * For the NEW normalized format (onboarding_progress table), use useOnboardingSteps hook.
 */

// ─── Old-format step definitions (flat columns on onboarding_checklist) ────

export const OLD_STEPS = [
  { key: 'general_onboarding',    label: 'General Onboarding',       icon: '📚', category: 'admin_managed' },
  { key: 'project_specific',      label: 'Project-Specific',         icon: '🎓', category: 'admin_managed' },
  { key: 'welcome_email_docs',    label: 'Welcome Email + Docs',     icon: '📧', category: 'admin_managed' },
  { key: 'deel_onboarding',       label: 'Deel',                     icon: '💳', category: 'integration' },
  { key: 'insightful_onboarding', label: 'Insightful',               icon: '⏱',  category: 'integration' },
  { key: 'pipelines_onboarding',  label: 'Pipelines',                icon: '🔧', category: 'integration' },
  { key: 'slack_invitation',      label: 'Slack',                    icon: '💬', category: 'integration' },
];

// Map from old flat column keys to new step_library IDs
export const OLD_TO_NEW_STEP_MAP = {
  'general_onboarding':    'general_onboarding',
  'project_specific':      'project_specific',
  'welcome_email_docs':    'welcome_email_docs',
  'deel_onboarding':       'tool_setup_deel',
  'insightful_onboarding': 'tool_setup_insightful',
  'pipelines_onboarding':  'tool_setup_pipelines',
  'slack_invitation':      'slack_invitation',
};

const PIPELINE_STEP_IDS = new Set(['pipelines_onboarding', 'tool_setup_pipelines']);

// ─── Status helpers ────────────────────────────────────────

export function getStepStatus(checklistRow, stepKey) {
  if (!checklistRow) return 'Not Started';
  return checklistRow[stepKey] || 'Not Started';
}

export function getStatusColor(status) {
  switch (status) {
    case 'Completed': return 'var(--accent-teal)';
    case 'In Progress': return 'var(--accent-gold)';
    case 'Skipped': return 'var(--text-muted)';
    case 'N/A': return 'var(--text-muted)';
    default: return 'var(--text-muted)';
  }
}

export function getStatusIcon(status) {
  switch (status) {
    case 'Completed': return '✓';
    case 'In Progress': return '◐';
    case 'Skipped': return '⊘';
    case 'N/A': return '—';
    default: return '○';
  }
}

export function nextStatus(current) {
  switch (current) {
    case 'Not Started': return 'In Progress';
    case 'In Progress': return 'Completed';
    case 'Completed': return 'Not Started';
    default: return 'Not Started';
  }
}

// ─── Completion helpers ────────────────────────────────────

/**
 * Count completed steps from an old-format checklist row.
 * @param {Object} checklistRow - row from onboarding_checklist (flat columns)
 * @param {Array} steps - array of { key } objects (defaults to OLD_STEPS)
 * @param {Object} job - optional job object to check uses_pipelines
 */
export function countCompleted(checklistRow, steps = OLD_STEPS, job = null) {
  if (!checklistRow) return { completed: 0, total: 0 };
  const applicableSteps = getApplicableSteps(steps, job);
  const completed = applicableSteps.filter(s => getStepStatus(checklistRow, s.key) === 'Completed').length;
  return { completed, total: applicableSteps.length };
}

/**
 * Check if all required steps are completed.
 */
export function areAllDone(checklistRow, steps = OLD_STEPS, job = null) {
  const { completed, total } = countCompleted(checklistRow, steps, job);
  return completed === total && total > 0;
}

/**
 * Filter steps based on job config (e.g., exclude pipelines if job doesn't use them).
 */
export function isOnboardingStepApplicable(stepOrId, job = null) {
  const stepId = typeof stepOrId === 'string'
    ? stepOrId
    : stepOrId?.id || stepOrId?.step_id || stepOrId?.key;

  if (!stepId) return true;
  if (PIPELINE_STEP_IDS.has(stepId) && job?.uses_pipelines === false) return false;
  return true;
}

/**
 * Filter steps based on job config (e.g., exclude pipelines if job doesn't use them).
 */
export function getApplicableSteps(steps = OLD_STEPS, job = null) {
  return steps.filter(step => isOnboardingStepApplicable(step, job));
}

/**
 * Build a completed checklist object (all steps = 'Completed').
 * Used for bypass/fast-track operations.
 */
export function buildCompletedChecklist() {
  const result = {};
  OLD_STEPS.forEach(s => { result[s.key] = 'Completed'; });
  result.updated_at = new Date().toISOString();
  return result;
}

/**
 * Build a default checklist object (all steps = 'Not Started').
 * Used when creating new onboarding checklists.
 */
export function buildDefaultChecklist() {
  const result = {};
  OLD_STEPS.forEach(s => { result[s.key] = 'Not Started'; });
  return result;
}

export default {
  OLD_STEPS,
  OLD_TO_NEW_STEP_MAP,
  getStepStatus,
  getStatusColor,
  getStatusIcon,
  nextStatus,
  countCompleted,
  areAllDone,
  isOnboardingStepApplicable,
  getApplicableSteps,
  buildCompletedChecklist,
  buildDefaultChecklist,
};
